import React, { useState, useEffect } from 'react';
import { collection, query, onSnapshot, doc, addDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../App';
import { appId } from '../config/firebaseConfig';
import useConfirmationModal from '../components/common/useConfirmationModal';

export default function ManageTicketInfoPage({ navigateTo }) {
    const [infoItems, setInfoItems] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Estados do Formulário
    const [name, setName] = useState('');
    const [fields, setFields] = useState([]); // Array de objetos { name: '', type: 'text' }
    const [editingId, setEditingId] = useState(null);
    
    const { ConfirmationModal, showConfirmation } = useConfirmationModal();
    const collectionPath = `/artifacts/${appId}/public/data/launch_infos`;

    useEffect(() => {
        const q = query(collection(db, collectionPath));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setInfoItems(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });
        return () => unsubscribe();
    }, [collectionPath]);

    // Gerenciamento de Campos Internos (Hostname, RAM, etc)
    const addField = () => setFields([...fields, { name: '', type: 'text', options: '' }]);
    
    const removeField = (index) => {
        const newFields = fields.filter((_, i) => i !== index);
        setFields(newFields);
    };

    const updateField = (index, key, value) => {
        const newFields = [...fields];
        newFields[index][key] = value;
        setFields(newFields);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;

        const data = { 
            name, 
            fields: fields.filter(f => f.name.trim() !== ''), // Remove campos vazios antes de salvar
            updatedAt: new Date() 
        };

        try {
            if (editingId) {
                await updateDoc(doc(db, collectionPath, editingId), data);
                setEditingId(null);
            } else {
                await addDoc(collection(db, collectionPath), { ...data, createdAt: new Date() });
            }
            setName('');
            setFields([]);
        } catch (error) { console.error("Erro ao salvar lançamento:", error); }
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        setName(item.name || '');
        setFields(item.fields || []); // Carrega os campos existentes para edição
    };

    const handleCancel = () => {
        setEditingId(null);
        setName('');
        setFields([]);
    };

    return (
        <div className="p-4 md:p-8 font-sans text-white bg-slate-900 min-h-screen">
            <ConfirmationModal />
            <header className="mb-8">
                <button onClick={() => navigateTo('tickets')} className="text-cyan-400 hover:text-cyan-300 flex items-center gap-2 mb-2 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                    Voltar para Chamados
                </button>
                <h1 className="text-4xl font-bold text-cyan-400">Gerenciar Lançamentos</h1>
                <p className="text-slate-400 mt-2">Configure os tipos de informações e campos disponíveis para os chamados.</p>
            </header>

            {/* FORMULÁRIO DE EDIÇÃO/CADASTRO */}
            <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 mb-8 shadow-xl">
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="flex gap-4 items-end font-sans">
                        <div className="flex-1">
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Nome do Grupo de Informação</label>
                            <input 
                                type="text" placeholder="Ex: Informações do PC" value={name} 
                                onChange={e => setName(e.target.value)} 
                                className="w-full bg-slate-700 border border-slate-600 rounded-md p-3 text-white outline-none focus:ring-2 focus:ring-cyan-400 transition-all" 
                                required 
                            />
                        </div>
                        <button type="submit" className="bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-3 px-8 rounded-md shadow-lg transition-all">
                            {editingId ? 'Salvar Alterações' : 'Criar Novo Lançamento'}
                        </button>
                        {editingId && (
                            <button type="button" onClick={handleCancel} className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-3 px-8 rounded-md transition-all">
                                Cancelar
                            </button>
                        )}
                    </div>

                    {/* SEÇÃO DE CAMPOS INTERNOS (Hostname, RAM, etc) */}
                    <div className="border-t border-slate-700 pt-6">
                        <div className="flex justify-between items-center mb-4 font-sans">
                            <h2 className="text-lg font-bold text-cyan-300">Configurar Campos Internos</h2>
                            <button type="button" onClick={addField} className="text-xs bg-slate-700 hover:bg-slate-600 border border-slate-600 px-3 py-1 rounded text-cyan-400 font-bold transition-all">
                                + Adicionar Campo
                            </button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {fields.map((field, index) => (
                                <div key={index} className="bg-slate-900/50 p-4 rounded-lg border border-slate-700 space-y-3 relative group">
                                    <div className="flex flex-col md:flex-row gap-3">
                                        <div className="flex-1">
                                            <label className="block text-[8px] font-bold text-slate-500 uppercase tracking-widest mb-1">Nome do Campo</label>
                                            <input 
                                                type="text" 
                                                placeholder="Ex: Hostname ou SO" 
                                                value={field.name} 
                                                onChange={(e) => updateField(index, 'name', e.target.value)}
                                                className="w-full bg-slate-800 border border-slate-700 rounded p-2 text-white text-xs outline-none focus:ring-1 focus:ring-cyan-500"
                                            />
                                        </div>
                                        <div className="w-full md:w-40">
                                            <label className="block text-[8px] font-bold text-slate-500 uppercase tracking-widest mb-1">Tipo</label>
                                            <select
                                                value={field.type || 'text'}
                                                onChange={(e) => updateField(index, 'type', e.target.value)}
                                                className="w-full bg-slate-800 border border-slate-700 rounded p-2 text-white text-xs outline-none focus:ring-1 focus:ring-cyan-500 font-bold"
                                            >
                                                <option value="text">Texto</option>
                                                <option value="select">Seleção</option>
                                            </select>
                                        </div>
                                    </div>
                                    {field.type === 'select' && (
                                        <div className="animate-fadeIn">
                                            <label className="block text-[8px] font-bold text-slate-500 uppercase tracking-widest mb-1">Opções (Separadas por vírgula)</label>
                                            <input 
                                                type="text" 
                                                placeholder="Ex: Windows 10, Windows 11, Linux, macOS" 
                                                value={field.options || ''} 
                                                onChange={(e) => updateField(index, 'options', e.target.value)}
                                                className="w-full bg-slate-800 border border-slate-700 rounded p-2 text-white text-xs outline-none focus:ring-1 focus:ring-cyan-500"
                                            />
                                        </div>
                                    )}
                                    <button 
                                        type="button" 
                                        onClick={() => removeField(index)} 
                                        className="absolute top-2 right-2 text-slate-500 hover:text-red-400 p-1 opacity-0 group-hover:opacity-100 transition-all"
                                        title="Remover Campo"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </button>
                                </div>
                            ))}
                            {fields.length === 0 && <p className="text-slate-500 text-xs italic py-2 col-span-2">Nenhum campo adicionado a este lançamento.</p>}
                        </div>
                    </div>
                </form>
            </div>

            {/* LISTAGEM DE GRUPOS CADASTRADOS */}
            <div className="space-y-3">
                {loading ? <p className="text-center text-slate-500 py-10">Carregando...</p> : infoItems.map(item => (
                    <div key={item.id} className={`bg-slate-800 p-5 rounded-lg border flex justify-between items-center transition-all ${editingId === item.id ? 'border-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.2)]' : 'border-slate-700 hover:border-slate-500'}`}>
                        <div>
                            <span className="text-xl font-semibold text-cyan-300">{item.name}</span>
                            <div className="flex gap-2 mt-1 flex-wrap">
                                {item.fields?.map((f, i) => (
                                    <span key={i} className="text-[9px] bg-slate-900 text-slate-500 px-2 py-0.5 rounded border border-slate-700 uppercase font-bold tracking-tighter">
                                        {f.name} ({f.type === 'select' ? 'Seleção' : 'Texto'})
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div className="flex gap-4">
                            <button onClick={() => handleEdit(item)} className="text-slate-400 hover:text-cyan-300 transition-colors">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L16.732 3.732z" /></svg>
                            </button>
                            <button onClick={() => showConfirmation({title: "Excluir?", message: "Apagar este grupo e todos os seus campos?", onConfirm: async () => await deleteDoc(doc(db, collectionPath, item.id))})} className="text-red-500 hover:text-red-400 transition-colors">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}